import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Combobox } from "../components/ui/combobox";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, Edit, Archive, Download, RefreshCw, AlertCircle, Trash2, FileText } from "lucide-react";
import jsPDF from "jspdf";
import type { TrainingPlan as TrainingPlanBase, TrainingPlanStatus, ProcedureCheck, User } from "../types";
import { toast } from "sonner";
import { MultiSelect } from "../components/ui/multi-select";
import { cn } from "../components/ui/utils";
import { Label } from "../components/ui/label";
import FileAttachments from "../components/FileAttachments";
import { getMatchedObjects, toSydneyYYYYMMDD, getUser, formatArchiveDate, toSydneyDate, formatTimestamp } from "../utils/helpers";
import CustomEditor from "../components/ui/custom-text-editor";
import { Can } from "../components/Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { ActivityLog } from "../components/ActivityLog";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

type ProcedureChecks = {
  [procedureId: string]: ProcedureCheck;
};

// Extend TrainingPlan locally to include archive fields
type TrainingPlan = TrainingPlanBase & {
  archived?: boolean;
  archivedOn?: string;
  archivedBy?: string;
  archivedReason?: string;
  procedureChecks?: ProcedureChecks;
  attachments?: Array<{
    id: string;
    filename: string;
    key: string;
    url: string;
    size: number;
    mimetype: string;
    uploadedBy: string;
    uploadedAt: string;
  }>;
};

const getIdString = (idOrObj: any) =>
  typeof idOrObj === 'object' && idOrObj !== null && idOrObj.id
    ? String(idOrObj.id)
    : String(idOrObj);

const getTrainingPlan = async (id: any) => {
  const token = localStorage.getItem("token");
  const response = await fetch(`${API_BASE}/api/trainingplans/${id}`, {
    method: "GET",
    headers: {
      "Authorization": `Bearer ${token}`,
      "Content-Type": "application/json",
    }
  });

  const data = await response.json();

  return data.trainingPlan || null;
}

export default function TrainingPlansPageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    users,
    selectionUsers,
    trainingPlans,
    updateTrainingPlan,
    procedures,
    addTrainingPlan,
    updateTrainingProcedureCheck,
  } = useData();

  const [plan, setPlan] = useState<TrainingPlan | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [unarchiving, setUnarchiving] = useState(false);
  const [status, setStatus] = useState<TrainingPlanStatus>("Not Started");
  const [statusDisabled, setStatusDisabled] = useState(false);
  const [statusHelper, setStatusHelper] = useState("");
  const [procedureChecks, setProcedureChecks] = useState<ProcedureChecks>({});
  const [isLoading, setIsLoading] = useState(true);
  const [openUnarchive, setOpenUnarchive] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [planToDelete, setPlanToDelete] = useState<TrainingPlan | null>(null);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [selectedProcedureId, setSelectedProcedureId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [openArchive, setOpenArchive] = useState(false);
  const [archiveReason, setArchiveReason] = useState("");
  const quillRef = useRef<any>(null);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [archivedBy, setArchivedBy] = useState<User | null>(null);
  const [allowedStatuses, setAllowedStatuses] = useState<TrainingPlanStatus[]>([]);

  const getUserId = (idOrObj: any) =>
    typeof idOrObj === 'object' && idOrObj !== null && idOrObj.id
      ? String(idOrObj.id)
      : String(idOrObj);

  useEffect(() => {
    const fetchTrainingPlan = async () => {
      if (!id) return;
      // const found = trainingPlans.find((p) => p.id == id) as TrainingPlan | undefined;
      const found = await getTrainingPlan(id);
      if (found) {
        setPlan(found);
        setStatus(found.status);
        setAttachments(found.attachments || []);

        // Initialize procedureChecks for ALL procedures in the training plan
        setProcedureChecks(found.procedureChecks);
      }
      setIsLoading(false);
    }
    fetchTrainingPlan();
  }, [id, trainingPlans]);

  // Validation: All checkboxes must be ticked to enable Completed
  const canComplete = plan && (plan.procedureChecks || []).every((procedureCheck: any) => {
    return procedureCheck?.explained && procedureCheck?.demonstrated;
  });

  // useEffect(() => {
  //   if (!plan) return;
  //   if (status === "Completed" && !canComplete) {
  //     setStatusDisabled(true);
  //     setStatusHelper(
  //       "Cannot mark as Completed until all procedures are explained and demonstrated."
  //     );
  //   } else {
  //     setStatusDisabled(false);
  //     setStatusHelper("");
  //   }
  // }, [status, procedureChecks, plan, canComplete]);

  useEffect(() => {
    if (!plan) return;

    // Determine allowed next statuses based on the current status
    switch (plan.status) {
      case "Not Started":
        setAllowedStatuses(["In Progress"]);
        break;
      case "In Progress":
        setAllowedStatuses(["Completed"]);
        break;
      case "Completed":
        setAllowedStatuses([]); // No further status changes allowed
        break;
      default:
        setAllowedStatuses([]);
    }

    if (status === "Completed" && !canComplete) {
      setStatusDisabled(true);
      setStatusHelper(
        "Cannot mark as Completed until all procedures are explained and demonstrated."
      );
    } else {
      setStatusDisabled(false);
      setStatusHelper("");
    }
  }, [status, procedureChecks, plan, canComplete]);

  useEffect(() => {
    if (plan && typeof plan.archivedBy !== 'string') setArchivedBy(plan.archivedBy || null);
  }, [plan]);

  if (isLoading) return <div>Loading...</div>;
  if (!plan) return <div>Training Plan not found</div>;



  // Permissions
  const canEdit = ["Admin", "Director", "Senior Manager", "Manager"].includes(currentUser?.userRole ?? "");
  const canArchive = canEdit;
  const canChangeStatus =
    canEdit || (currentUser?.userRole === "Worker" && status !== "Completed");

  // User lookups
  const trainee = users.find((u) => getUserId(u.id) === getUserId(plan?.traineeId));
  const trainer = users.find((u) => getUserId(u.id) === getUserId(plan?.trainerId));

  // PDF Download
  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20; // Equal left and right margins
    const maxWidth = pageWidth - (margin * 2);
    let y = margin;

    // Helper function to add wrapped text
    const addWrappedText = (text: string, x: number, currentY: number, maxLineWidth: number) => {
      const lines = doc.splitTextToSize(text, maxLineWidth);
      doc.text(lines, x, currentY);
      return currentY + (lines.length * 7); // 7 is line height
    };

    // Helper function to check if we need a new page
    const checkNewPage = (currentY: number, linesNeeded: number = 1) => {
      if (currentY + (linesNeeded * 7) > pageHeight - margin) {
        doc.addPage();
        return margin;
      }
      return currentY;
    };

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    y = addWrappedText(`Training Plan: ${plan.trainingPlanName}`, margin, y, maxWidth);
    y += 10;

    // Plan Details
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);

    y = checkNewPage(y);
    y = addWrappedText(`Trainee: ${trainee?.name || "Unknown"}`, margin, y, maxWidth);
    y = addWrappedText(`Trainer: ${trainer?.name || "Unknown"}`, margin, y, maxWidth);
    y = addWrappedText(`Status: ${plan.status}`, margin, y, maxWidth);
    y = addWrappedText(`Description: ${plan.description || "-"}`, margin, y, maxWidth);
    y = addWrappedText(`Created On: ${toSydneyDate(plan.createdAt)}`, margin, y, maxWidth);
    y = addWrappedText(`Started On: ${plan.startedOn ? toSydneyDate(plan.startedOn) : "-"}`, margin, y, maxWidth);
    y = addWrappedText(`Completed On: ${plan.completedOn ? toSydneyDate(plan.completedOn) : "-"}`, margin, y, maxWidth);
    y += 10;

    // Procedures Section
    y = checkNewPage(y);
    doc.setFont('helvetica', 'bold');
    y = addWrappedText("Procedures:", margin, y, maxWidth);
    y += 5;

    doc.setFont('helvetica', 'normal');
    (plan.procedures || []).forEach((procRef: any) => {
      const procId =
        typeof procRef === 'object' && procRef !== null && 'id' in procRef
          ? String(procRef.id)
          : String(procRef);
      const proc = procedures.find((p) => String(p.id) === procId);
      const procName = typeof proc === 'object' && proc !== null ? proc.procedureName : '';
      const explainedInfo = procedureChecks[procId]?.explained
        ? `Yes (${procedureChecks[procId]?.explainedDate || 'No date'})`
        : "No";
      const demonstratedInfo = procedureChecks[procId]?.demonstrated
        ? `Yes (${procedureChecks[procId]?.demonstratedDate || 'No date'})`
        : "No";

      y = checkNewPage(y, 3); // Check if we need space for procedure info
      y = addWrappedText(`• ${procName}`, margin + 5, y, maxWidth - 5);
      y = addWrappedText(`  Explained: ${explainedInfo}`, margin + 10, y, maxWidth - 10);
      y = addWrappedText(`  Demonstrated: ${demonstratedInfo}`, margin + 10, y, maxWidth - 10);
      y += 5;
    });

    // Notes Section
    y += 10;
    y = checkNewPage(y);
    doc.setFont('helvetica', 'bold');
    y = addWrappedText("Trainee Notes:", margin, y, maxWidth);
    y += 5;

    doc.setFont('helvetica', 'normal');
    let hasNotes = false;
    (plan.procedures || []).forEach((procRef: any) => {
      const procId =
        typeof procRef === 'object' && procRef !== null && 'id' in procRef
          ? String(procRef.id)
          : String(procRef);
      const proc = procedures.find((p) => String(p.id) === procId);
      const procName = typeof proc === 'object' && proc !== null ? proc.procedureName : '';
      const notes = procedureChecks[procId]?.traineeNotes;

      if (notes && notes.trim()) {
        hasNotes = true;
        y = checkNewPage(y, 4); // Check if we need space for notes
        doc.setFont('helvetica', 'bold');
        y = addWrappedText(`${procName}:`, margin + 5, y, maxWidth - 5);
        doc.setFont('helvetica', 'normal');
        // Strip HTML tags from notes for PDF
        const plainTextNotes = notes.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ');
        y = addWrappedText(plainTextNotes, margin + 10, y, maxWidth - 10);
        y += 8;
      }
    });

    if (!hasNotes) {
      y = addWrappedText("No notes recorded.", margin + 5, y, maxWidth - 5);
    }

    // Attachments Section
    y += 15;
    y = checkNewPage(y);
    doc.setFont('helvetica', 'bold');
    y = addWrappedText("Attachments:", margin, y, maxWidth);
    y += 5;

    doc.setFont('helvetica', 'normal');
    if (attachments && attachments.length > 0) {
      attachments.forEach((attachment) => {
        y = checkNewPage(y, 2);
        y = addWrappedText(`• ${attachment.filename}`, margin + 5, y, maxWidth - 5);
        y = addWrappedText(`  Size: ${(attachment.size / 1024 / 1024).toFixed(2)} MB, Uploaded: ${toSydneyDate(attachment.uploadedAt)}`, margin + 10, y, maxWidth - 10);
        y += 3;
      });
    } else {
      y = addWrappedText("No attachments.", margin + 5, y, maxWidth - 5);
    }

    // Activity Log Section
    y += 15;
    y = checkNewPage(y);
    doc.setFont('helvetica', 'bold');
    y = addWrappedText("Activity Log:", margin, y, maxWidth);
    y += 5;

    doc.setFont('helvetica', 'normal');
    if (plan.activityLog && plan.activityLog.length > 0) {
      const sortedLog = [...plan.activityLog].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      sortedLog.forEach((log) => {
        const logUser = users.find(u => u.id === (typeof log.user !== 'string' ? log.user.id : log.user));
        const userName = logUser?.name || "Unknown";
        const timestamp = formatTimestamp(log.timestamp);

        y = checkNewPage(y, 3);
        y = addWrappedText(`${timestamp} - ${userName}`, margin + 5, y, maxWidth - 5);
        y = addWrappedText(`${log.changeDescription}`, margin + 10, y, maxWidth - 10);
        y += 5;
      });
    } else {
      y = addWrappedText("No activity logged yet.", margin + 5, y, maxWidth - 5);
    }

    doc.save(`${plan.trainingPlanName || "training-plan"}.pdf`);
  };

  // Handle checkboxes
  const handleCheckboxChange = (procId: string, field: "explained" | "demonstrated", value: boolean) => {
    const updatedProcedureCheck: Partial<ProcedureCheck> = {};
    updatedProcedureCheck[field] = value;

    updateTrainingProcedureCheck(plan.id, procId, updatedProcedureCheck, true);
  };

  // Handle date changes
  const handleDateChange = (procId: string, field: "explainedDate" | "demonstratedDate", date: string) => {
    const updatedProcedureCheck: Partial<ProcedureCheck> = {};
    updatedProcedureCheck[field] = date;

    updateTrainingProcedureCheck(plan.id, procId, updatedProcedureCheck, true);
  };

  // Handle status change
  const handleStatusChange = (newStatus: TrainingPlanStatus) => {
    setStatus(newStatus);
    if (plan && newStatus !== plan.status) {
      // Create activity log entry for status change
      const activityLogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        user: currentUser || '',
        changeDescription: `Training plan status changed from "${plan.status}" to "${newStatus}"`
      };

      const updatedActivityLog = plan.activityLog ? [...plan.activityLog, activityLogEntry] : [activityLogEntry];

      updateTrainingPlan(plan.id, {
        status: newStatus,
        startedOn:
          newStatus === "In Progress" && !plan.startedOn
            ? new Date().toISOString()
            : plan.startedOn,
        completedOn:
          newStatus === "Completed" && !plan.completedOn
            ? new Date().toISOString()
            : plan.completedOn,
        activityLog: updatedActivityLog
      });
    }
  };

  // Archive/Unarchive
  const handleArchive = async () => {
    if (!plan) return;
    setArchiving(true);
    const updates: Partial<TrainingPlan> = {
      archived: true,
      archivedOn: new Date().toISOString(),
      archivedBy: currentUser?.id,
      archivedReason: archiveReason.trim() || undefined,
    };
    await updateTrainingPlan(plan.id, updates, false);
    setArchiving(false);
    setOpenArchive(false);
    setArchiveReason("");
    toast.success("Training plan archived successfully");
    // Update local state to reflect archived status
    setPlan({ ...plan, ...updates });
  };
  const handleUnarchive = async () => {
    if (!plan) return;
    setUnarchiving(true);

    await updateTrainingPlan(plan.id, {
      archived: false,
      archivedOn: null,
      archivedBy: null,
    });

    // try {
    //   // Use the dedicated unarchive endpoint that properly removes fields
    //   const response = await fetch(`${API_BASE}/api/trainingplans/${plan.id}/unarchive`, {
    //     method: 'PATCH',
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'Authorization': `Bearer ${localStorage.getItem('token')}`,
    //     },
    //   });

    //   if (response.ok) {
    //     const { trainingPlan } = await response.json();
    //     // Update local state with the unarchived plan
    //     setPlan(trainingPlan);
    //     toast.success("Training plan unarchived successfully");
    //   } else {
    //     toast.error("Failed to unarchive training plan");
    //   }
    // } catch (error) {
    //   console.error('Error unarchiving:', error);
    //   toast.error("Failed to unarchive training plan");
    // }

    setUnarchiving(false);
    setOpenUnarchive(false);
    // Do not navigate away; stay on the current page
  };

  const selectedProcedures = plan.procedures;
  const maxDisplay = 3;
  const displayProcedures = selectedProcedures.slice(0, maxDisplay);
  const hiddenCount = selectedProcedures.length - displayProcedures.length;

  const handleDelete = async () => {
    if (!plan || deleteConfirmation !== "delete") return;

    // Create activity log entry for deletion
    const activityLogEntry = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      user: currentUser || '',
      changeDescription: 'Training plan deleted'
    };

    const updatedActivityLog = plan.activityLog ? [...plan.activityLog, activityLogEntry] : [activityLogEntry];

    await updateTrainingPlan(plan.id, {
      deleted: true,
      deletedOn: new Date().toISOString(),
      deletedBy: currentUser?.id,
      activityLog: updatedActivityLog
    }, false);

    setOpenDelete(false);
    setPlanToDelete(null);
    setDeleteConfirmation("");
    toast.success("Training plan deleted successfully");
    navigate('/training-plans');
  };

  // Handle notes
  const handleOpenNotes = (procId: string, notes: string) => {
    setSelectedProcedureId(procId);
    setNoteText(notes);
    setOpenNotesDialog(true);
  };

  // Handle timestamp insertion
  const handleNotesChange = (value: string) => {
    console.log('Notes changed, value:', value);

    // Check for /t pattern in the HTML content
    if (value.includes('/t') && !value.includes('data:image')) {
      console.log('Found /t pattern');

      const currentDate = toSydneyDate(new Date());
      const userName = currentUser?.name || 'Unknown User';
      const timestamp = `${currentDate}, ${userName}:  `;

      // Replace the last occurrence of /t with timestamp
      const lastIndex = value.lastIndexOf('/t');
      if (lastIndex !== -1) {
        const newValue = value.substring(0, lastIndex) + timestamp + value.substring(lastIndex + 2);
        console.log('Setting new value:', newValue);
        setNoteText(newValue);

        // Set cursor position after the timestamp and space
        setTimeout(() => {
          if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            // Get the current text length to position cursor at the end
            const textLength = quill.getLength();
            quill.setSelection(textLength - 1); // -1 because Quill adds a newline at the end
            console.log('Set cursor position to end of text:', textLength - 1);
          }
        }, 10); // Slightly longer delay to ensure content is rendered
        return;
      }
    }

    setNoteText(value);
  };

  const handleSaveNotes = async () => {
    if (!plan || !selectedProcedureId) return;

    try {
      const updatedProcedureCheck: Partial<ProcedureCheck> = {};
      updatedProcedureCheck.traineeNotes = noteText;

      updateTrainingProcedureCheck(plan.id, selectedProcedureId, updatedProcedureCheck, true);
      setNoteText(noteText);
    } catch (error) {
      console.error('Error saving notes:', error);
      toast.error("Failed to save notes");
    }

    setOpenNotesDialog(false);
  };

  // Handle attachments change
  const handleAttachmentsChange = (newAttachments: any[]) => {
    setAttachments(newAttachments);

    const fetchTrainingPlan = async () => {
      const found = await getTrainingPlan(plan.id);
      if (found) {
        setPlan(found);
      }
    }
    fetchTrainingPlan();
  };

  return (
    <div className="space-y-6 p-2 md:p-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link to="/training-plans">
            <Button variant="outline" size="icon" className="flex-shrink-0">
              <ArrowLeft size={16} />
            </Button>
          </Link>
          <div className="flex flex-col">
            <h1 className="text-xl md:text-2xl font-bold leading-tight">{plan.trainingPlanName}</h1>
            <div className="flex gap-2 mt-1">
              <Badge variant="outline" className="bg-purple-100 text-purple-800">Training Plan</Badge>
              {plan.archived && (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800"><Archive className="w-3 h-3 mr-1" /> Archived</Badge>
              )}
            </div>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="w-full md:w-auto">
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
            {!plan.archived && (
              <Can I={ACTIONS.UPDATE} a={RESOURCES.TRAINING_PLAN}>
                <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto" disabled={plan.status === "Completed"} onClick={() => navigate(`/training-plans/${plan.id}/edit`)}>
                  <Edit className="w-4 h-4" /><span>Edit</span>
                </Button>
              </Can>
            )}
            {!plan.archived && (
              <Can I={ACTIONS.ARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto" onClick={() => setOpenArchive(true)} disabled={archiving}>
                  <Archive className="w-4 h-4" /><span>Archive</span>
                </Button>
              </Can>
            )}
            {plan.archived && (
              <Can I={ACTIONS.UNARCHIVE} a={RESOURCES.TRAINING_PLAN}>
                <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto" onClick={() => setOpenUnarchive(true)} disabled={unarchiving}>
                  <RefreshCw className="w-4 h-4" /><span>Unarchive</span>
                </Button>
              </Can>
            )}

            <Can I={ACTIONS.DOWNLOAD} a={RESOURCES.TRAINING_PLAN_PDF}>
              <Button variant="outline" className="flex items-center gap-2 w-full sm:w-auto" onClick={handleDownloadPDF}>
                <Download className="w-4 h-4" /><span>Download</span>
              </Button>
            </Can>

            {!plan.archived && (
              <Can I={ACTIONS.DELETE} a={RESOURCES.TRAINING_PLAN}>
                <Button variant="destructive" className="flex items-center gap-2 w-full sm:w-auto" disabled={plan.status === "Completed"} onClick={() => setOpenDelete(true)}>
                  <Trash2 className="w-4 h-4" /><span>Delete</span>
                </Button>
              </Can>
            )}
          </div>
        </div>
      </div>

      {/* Archive Banner */}
      {plan.archived && (
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800">This training plan is archived</h3>
                <p className="text-sm text-yellow-700">Archived on {formatArchiveDate(plan.archivedOn || "")} by {archivedBy?.name || "Unknown"}.</p>
                {plan.archivedReason && <p className="text-sm text-yellow-700 mt-1"><strong>Reason:</strong> {plan.archivedReason}</p>}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="training" className="w-full">
        <div className="w-full overflow-x-auto">
          <TabsList className="bg-gray-100 p-1 rounded-full w-full md:w-fit">
            <TabsTrigger value="training" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Training</TabsTrigger>
            <TabsTrigger value="log" className="rounded-full px-4 py-2 data-[state=active]:bg-white">Activity Log</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="training" className="space-y-6">
          {/* Plan Details */}
          <Card>
            <CardHeader><CardTitle>Plan Details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div><dt className="text-sm text-muted-foreground">Trainee</dt><dd>{plan.trainee ? <Link to={`/people/${plan.trainee.id}`} className="text-primary hover:underline">{plan.trainee.name}</Link> : "Unknown"}</dd></div>
                  <div><dt className="text-sm text-muted-foreground">Trainer</dt><dd>{plan.trainer ? <Link to={`/people/${plan.trainer.id}`} className="text-primary hover:underline">{plan.trainer.name}</Link> : "-"}</dd></div>
                  <div><dt className="text-sm text-muted-foreground">Created On</dt><dd>{toSydneyDate(plan.createdAt)}</dd></div>
                </div>
                <div className="space-y-4">
                  <div>
                    <dt className="text-sm text-muted-foreground">Status</dt>
                    <dd>
                      <Select value={status} onValueChange={handleStatusChange} disabled={!canChangeStatus || plan.archived || plan.status === "Completed"}>
                        <SelectTrigger><SelectValue placeholder="Select Status" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Not Started" disabled>Not Started</SelectItem>
                          <SelectItem value="In Progress" disabled={!allowedStatuses.includes("In Progress")}>In Progress</SelectItem>
                          <SelectItem value="Completed" disabled={!allowedStatuses.includes("Completed") || !canComplete}>Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      {statusDisabled && <span className="text-xs text-red-500 mt-1 block">{statusHelper}</span>}
                    </dd>
                  </div>
                  <div><dt className="text-sm text-muted-foreground">Started On</dt><dd><Input type="date" value={plan.startedOn ? toSydneyYYYYMMDD(plan.startedOn) : ""} onChange={e => updateTrainingPlan(plan.id, { startedOn: e.target.value ? new Date(e.target.value).toISOString() : undefined })} disabled={!canEdit || plan.archived} className="w-full md:w-auto" /></dd></div>
                  <div><dt className="text-sm text-muted-foreground">Completed On</dt><dd><Input type="date" value={plan.completedOn ? toSydneyYYYYMMDD(plan.completedOn) : ""} onChange={e => updateTrainingPlan(plan.id, { completedOn: e.target.value ? new Date(e.target.value).toISOString() : undefined })} disabled={!canEdit || !plan.startedOn || plan.archived || !canComplete} min={plan.startedOn ? toSydneyYYYYMMDD(plan.startedOn) : undefined} className="w-full md:w-auto" /></dd></div>
                </div>
                <div className="md:col-span-2"><dt className="text-sm text-muted-foreground">Description</dt><dd className="whitespace-pre-wrap">{plan.description || "-"}</dd></div>
              </div>
            </CardContent>
          </Card>

          {/* Procedures */}
          <Card>
            <CardHeader>
              <CardTitle>Procedures</CardTitle>
              <CardDescription>Each procedure must be explained and demonstrated to complete the plan.</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Mobile View: List */}
              <div className="md:hidden space-y-4">
                {(plan.procedureChecks || []).map((procedureCheck: any) => {
                  const procId = procedureCheck.procedure.id;
                  const procName = procedureCheck.procedure.procedureName;
                  const hasNotes = !!procedureCheck?.traineeNotes;

                  return (
                    <div key={procId} className="p-4 border rounded-lg space-y-4">
                      {/* Procedure Title and Notes Button */}
                      <div className="flex justify-between items-start">
                        <Link to={`/procedure/${procId}`} className="font-semibold text-primary hover:underline pr-2">
                          {procName}
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn("h-8 w-8 flex-shrink-0 -mt-1", hasNotes && "text-blue-600 bg-blue-50")}
                          onClick={() => handleOpenNotes(procId, procedureCheck?.traineeNotes)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Explained Section */}
                      <div className="space-y-2 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={`explained-mob-${procId}`} className="font-medium">Explained</Label>
                          <input
                            id={`explained-mob-${procId}`}
                            type="checkbox"
                            className="h-5 w-5 rounded"
                            checked={!!procedureCheck?.explained}
                            onChange={(e) => handleCheckboxChange(procId, "explained", e.target.checked)}
                            disabled={!canChangeStatus || plan.archived || plan.status === "Completed"}
                          />
                        </div>
                        <Input
                          type="date"
                          aria-label="Explained Date"
                          value={procedureCheck?.explained ? toSydneyYYYYMMDD(procedureCheck?.explainedDate) : ""}
                          onChange={(e) => handleDateChange(procId, "explainedDate", e.target.value)}
                          disabled={!procedureCheck?.explained || !canChangeStatus || plan.archived || plan.status === "Completed"}
                          className={cn("w-full h-9 text-sm", !procedureCheck?.explained && "opacity-50")}
                        />
                      </div>

                      {/* Demonstrated Section */}
                      <div className="space-y-2 p-3 bg-gray-50 rounded-md">
                        <div className="flex items-center justify-between">
                          <Label htmlFor={`demo-mob-${procId}`} className="font-medium">Demonstrated</Label>
                          <input
                            id={`demo-mob-${procId}`}
                            type="checkbox"
                            className="h-5 w-5 rounded"
                            checked={!!procedureCheck?.demonstrated}
                            onChange={(e) => handleCheckboxChange(procId, "demonstrated", e.target.checked)}
                            disabled={!canChangeStatus || plan.archived || plan.status === "Completed"}
                          />
                        </div>
                        <Input
                          type="date"
                          aria-label="Demonstrated Date"
                          value={procedureCheck?.demonstrated ? toSydneyYYYYMMDD(procedureCheck?.demonstratedDate) : ""}
                          onChange={(e) => handleDateChange(procId, "demonstratedDate", e.target.value)}
                          disabled={!procedureCheck?.demonstrated || !canChangeStatus || plan.archived || plan.status === "Completed"}
                          className={cn("w-full h-9 text-sm", !procedureCheck?.demonstrated && "opacity-50")}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>


              {/* Desktop View: Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Procedure</TableHead>
                      <TableHead>Explained</TableHead>
                      <TableHead>Explained Date</TableHead>
                      <TableHead>Demonstrated</TableHead>
                      <TableHead>Demonstrated Date</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(plan.procedureChecks || []).map((procedureCheck: any) => {
                      const procId = procedureCheck.procedure.id;
                      const procName = procedureCheck.procedure.procedureName;
                      const hasNotes = !!procedureCheck?.traineeNotes;
                      return (
                        <TableRow key={procId}>
                          <TableCell><Link to={`/procedure/${procId}`} className="text-primary hover:underline">{procName}</Link></TableCell>
                          <TableCell><input type="checkbox" checked={!!procedureCheck?.explained} onChange={(e) => handleCheckboxChange(procId, "explained", e.target.checked)} disabled={!canChangeStatus || plan.archived} /></TableCell>
                          <TableCell><Input type="date" value={procedureCheck?.explained ? toSydneyYYYYMMDD(procedureCheck?.explainedDate) : ""} onChange={(e) => handleDateChange(procId, "explainedDate", e.target.value)} disabled={!procedureCheck?.explained || !canChangeStatus || plan.archived} className="w-[140px]" /></TableCell>
                          <TableCell><input type="checkbox" checked={!!procedureCheck?.demonstrated} onChange={(e) => handleCheckboxChange(procId, "demonstrated", e.target.checked)} disabled={!canChangeStatus || plan.archived} /></TableCell>
                          <TableCell><Input type="date" value={procedureCheck?.demonstrated ? toSydneyYYYYMMDD(procedureCheck?.demonstratedDate) : ""} onChange={(e) => handleDateChange(procId, "demonstratedDate", e.target.value)} disabled={!procedureCheck?.demonstrated || !canChangeStatus || plan.archived} className="w-[140px]" /></TableCell>
                          <TableCell><Button variant="ghost" size="icon" className={cn("h-8 w-8", hasNotes && "text-blue-600")} onClick={() => handleOpenNotes(procId, procedureCheck?.traineeNotes)}><FileText className="h-4 w-4" /></Button></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          <FileAttachments
            resourceType="trainingplans"
            resourceId={plan.id}
            attachments={attachments}
            onAttachmentsChange={handleAttachmentsChange}
            // canEdit={canEdit}
            disabled={plan.archived || plan.status === "Completed"}
          />

          {/* Archive Training Plan Dialog */}
          <Dialog open={openArchive} onOpenChange={(open) => {
            setOpenArchive(open);
            if (!open) setArchiveReason("");
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Archive Training Plan</DialogTitle>
                <DialogDescription>
                  Are you sure you want to archive this training plan? You can unarchive it later if needed.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="archiveReason">Reason for archiving (optional)</Label>
                  <Textarea
                    id="archiveReason"
                    value={archiveReason}
                    onChange={(e) => setArchiveReason(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleArchive();
                      }
                    }}
                    placeholder="Enter reason for archiving this training plan..."
                    className="min-h-[100px]"
                    autoFocus
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setOpenArchive(false);
                  setArchiveReason("");
                }}>
                  Cancel
                </Button>
                <Button
                  onClick={handleArchive}
                  disabled={archiving}
                  className="bg-black text-white hover:bg-gray-800"
                >
                  {archiving ? "Archiving..." : "Archive"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Unarchive Training Plan Dialog */}
          <Dialog open={openUnarchive} onOpenChange={setOpenUnarchive}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Unarchive Training Plan</DialogTitle>
                <DialogDescription>
                  Are you sure you want to unarchive this training plan? It will become active again.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenUnarchive(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUnarchive}>
                  Unarchive
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Delete Training Plan Dialog */}
          <Dialog open={openDelete} onOpenChange={(open) => {
            setOpenDelete(open);
            if (open) setDeleteConfirmation("");
            if (!open) setDeleteConfirmation("");
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Delete Training Plan</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this training plan? Type the word "delete" below to confirm.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="deleteConfirmation">Confirmation</Label>
                  <Input
                    id="deleteConfirmation"
                    value={deleteConfirmation}
                    onChange={(e) => setDeleteConfirmation(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && deleteConfirmation === "delete") {
                        e.preventDefault();
                        handleDelete();
                      }
                    }}
                    placeholder="delete"
                    className="bg-gray-200"
                    autoFocus
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setOpenDelete(false);
                  setPlanToDelete(null);
                  setDeleteConfirmation("");
                }}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleteConfirmation !== "delete"}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Trainee Notes Dialog */}
          <Dialog open={openNotesDialog} onOpenChange={setOpenNotesDialog}>
            <DialogContent className="max-w-2xl h-[80vh] flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle>Trainee Notes</DialogTitle>
                <DialogDescription>
                  Jot down any insights from your training – it'll help you next time
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto">
                <div className="grid gap-4 py-4">
                  <div className="grid gap-2">
                    <p className="text-xs text-muted-foreground mb-2">
                      Tip: Type <code className="bg-muted px-1 py-0.5 rounded text-xs">/t</code> for datestamp
                    </p>
                    <div
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                          e.preventDefault();
                          handleSaveNotes();
                        }
                      }}
                    >
                      {/* <WrappedRichTextEditor
                    ref={quillRef}
                    value={noteText}
                    onChange={handleNotesChange}
                    placeholder="Enter notes about the trainee's progress with this procedure..."
                    className="min-h-[200px] max-h-[400px]"
                  /> */}
                      <CustomEditor
                        value={noteText}
                        onChange={handleNotesChange}
                        placeholder="Enter notes about the trainee's progress with this procedure..."
                        className="min-h-[200px] max-h-[400px]"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="flex-shrink-0">
                <div className="flex flex-col items-end">
                  <div className="text-sm text-muted-foreground mb-[10px]">
                    Save (Ctrl + Enter)
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setOpenNotesDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleSaveNotes}>
                      Save Notes
                    </Button>
                  </div>
                </div>
              </DialogFooter>
            </DialogContent>
          </Dialog>

        </TabsContent>

        <TabsContent value="log">
          <ActivityLog
            logs={plan.activityLog || []}
            entityName="Training Plan"
            users={users}
            currentUser={currentUser}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
} 